import i18n from "../i18n";

/**
 * Get translated attribute name based on current language
 * @param attribute - Attribute object with name fields
 * @returns Translated attribute name
 */
export const getAttributeName = (
  attribute:
    | {
        attribute_name?: string;
        attribute_name_az?: string;
        attribute_name_en?: string;
        attribute_name_es?: string;
        attribute_name_de?: string;
        name?: string;
        name_az?: string;
        name_en?: string;
        name_es?: string;
        name_de?: string;
      }
    | null
    | undefined,
): string => {
  if (!attribute) return "";

  const lang = i18n.language;

  // Try to get translated name from attribute_name fields (for product variant attributes)
  if (
    attribute.attribute_name_az !== undefined ||
    attribute.attribute_name_en !== undefined
  ) {
    switch (lang) {
      case "az":
        return attribute.attribute_name_az || attribute.attribute_name || "";
      case "en":
        return attribute.attribute_name_en || attribute.attribute_name || "";
      case "es":
        return attribute.attribute_name_es || attribute.attribute_name || "";
      case "de":
        return attribute.attribute_name_de || attribute.attribute_name || "";
      default:
        return attribute.attribute_name || "";
    }
  }

  // Try to get translated name from name fields (for attribute definitions)
  if (attribute.name_az !== undefined || attribute.name_en !== undefined) {
    switch (lang) {
      case "az":
        return attribute.name_az || attribute.name || "";
      case "en":
        return attribute.name_en || attribute.name || "";
      case "es":
        return attribute.name_es || attribute.name || "";
      case "de":
        return attribute.name_de || attribute.name || "";
      default:
        return attribute.name || "";
    }
  }

  // Fallback to base name
  return attribute.attribute_name || attribute.name || "";
};

/**
 * Get translated attribute value based on current language
 * @param attribute - Attribute object with value fields
 * @returns Translated attribute value
 */
export const getAttributeValue = (
  attribute:
    | {
        value?: string;
        value_az?: string;
        value_en?: string;
        value_es?: string;
        value_de?: string;
      }
    | null
    | undefined,
): string => {
  if (!attribute) return "";

  const lang = i18n.language;

  // Try to get translated value
  switch (lang) {
    case "az":
      return attribute.value_az || attribute.value || "";
    case "en":
      return attribute.value_en || attribute.value || "";
    case "es":
      return attribute.value_es || attribute.value || "";
    case "de":
      return attribute.value_de || attribute.value || "";
    default:
      return attribute.value || "";
  }
};

/**
 * Get translated attribute values array based on current language
 * @param attribute - Attribute object with values arrays
 * @returns Translated attribute values array
 */
export const getAttributeValues = (
  attribute:
    | {
        values?: { id: number; name: string }[];
        values_az?: { id?: number; name: string }[];
        values_en?: { id?: number; name: string }[];
        values_es?: { id?: number; name: string }[];
        values_de?: { id?: number; name: string }[];
      }
    | null
    | undefined,
): { id: number | string; name: string }[] => {
  if (!attribute) return [];

  const lang = i18n.language;

  // Try to get translated values array
  switch (lang) {
    case "az":
      return (
        attribute.values_az?.map((v) => ({
          id: v.id || v.name,
          name: v.name,
        })) ||
        attribute.values ||
        []
      );
    case "en":
      return (
        attribute.values_en?.map((v) => ({
          id: v.id || v.name,
          name: v.name,
        })) ||
        attribute.values ||
        []
      );
    case "es":
      return (
        attribute.values_es?.map((v) => ({
          id: v.id || v.name,
          name: v.name,
        })) ||
        attribute.values ||
        []
      );
    case "de":
      return (
        attribute.values_de?.map((v) => ({
          id: v.id || v.name,
          name: v.name,
        })) ||
        attribute.values ||
        []
      );
    default:
      return attribute.values || [];
  }
};
